import { useMemo, useState } from "react";
import Modal from "../../components/common/Modal";
import { FormikHelpers, useFormik } from "formik";
import { UserInterface, TUserFilter } from "../../interfaces/userInterface";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { ListSearchControls } from "../../components/common/data-table/ListSearchControls";
import { ListViewControls } from "../../components/common/data-table/ListViewControls";
import Button from "../../components/common/Buttons/Button";
import { faCheck, faEdit, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  userSchema,
  useUsers,
  useSaveUser,
  useUserStatusToggle,
  useUserByIdQuery,
  UserForm,
  UserTable,
  UserHeader,
} from "../../features/users";
import { useRoles, useRolesOptions } from "../../features/roles";

const formDataInit: UserInterface = {
  name: "",
  lastName: "",
  userName: "",
  password: "",
  active: true,
  roleId: "",
};

const filterInit: TUserFilter = {};

const UserPage = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const { pagination, handlePageChange, handlePageSizeChange, handleSort } =
    usePagination({
      initialPageSize: 10,
      initialSortBy: "name",
      initialSortOrder: "desc",
    });

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<TUserFilter>(filterInit);

  const handleSearch = () => {
    setFilter({ ...filterInit, search: searchTerm || undefined });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilter(filterInit);
  };

  const { users, paginationData, isLoading } = useUsers({
    filter,
    pagination,
  });

  const { roles } = useRoles({ filter: {}, pagination: { page: 1, pageSize: 50 } });
  const { rolesOptions } = useRolesOptions();

  const roleNameById = useMemo(() => {
    return roles.reduce<Record<string, string>>((acc, role) => {
      if (role._id) acc[role._id] = role.name;
      return acc;
    }, {});
  }, [roles]);

  const { data: userToEdit } = useUserByIdQuery(editingUserId ?? undefined);

  const { handleSave } = useSaveUser();
  const { handleEnable, handleDisable } = useUserStatusToggle();

  const formik = useFormik<UserInterface>({
    initialValues: userToEdit?.data || formDataInit,
    validationSchema: userSchema,
    enableReinitialize: true,
    onSubmit: (
      values: UserInterface,
      formikHelpers: FormikHelpers<UserInterface>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm({ values: formDataInit });
      handleCloseModal();
    },
  });

  const handleOpenNewUserModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingUserId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingUserId(null);
    setShowModal(false);
  };

  const handleEditUser = (user: UserInterface) => {
    const { _id: userId } = user;
    if (!userId) return;
    setEditingUserId(userId);
    setShowModal(true);
  };

  return (
    <div>
      <UserHeader />
      <ListSearchControls
        searchLabel="Buscar por nombre"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />
      <ListViewControls
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
      >
        <Button size="medium" onClick={handleOpenNewUserModal} color="primary">
          Nuevo
        </Button>
      </ListViewControls>
      <UserTable
        data={users}
        isLoading={isLoading}
        roleNameById={roleNameById}
        sortBy={pagination.sortBy as keyof UserInterface}
        sortOrder={pagination.sortOrder}
        onSort={handleSort}
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: handleEditUser,
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id!),
            shouldShow: (row) => !!row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id!),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleCloseModal} submit={formik.handleSubmit}>
        <UserForm formik={formik} rolesOptions={rolesOptions} />
      </Modal>
    </div>
  );
};

export default UserPage;
